import {useUserContext} from 'authentication/UserContext';
import {GetHeadlessRegulatoryDocumentsDocument} from 'features/HeadlessRegulatoryDocuments/hooks/headlessRegulatoryDocuments.generated';
import {GetRegulationDocument} from 'features/Regulations/hooks';
import {useGetRegulatoryDocumentEditFormQuery} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentEditForm.generated';
import {useCommand} from 'hooks';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Market, UserRole, WorkflowStatus} from 'types';
import {useRegulatoryDocumentsContext} from '../context';
import {GetWorkbenchItemsDocument} from 'features/Dashboard/hooks/workbenchItems.generated';
import {
	RegulatoryDocumentsForm,
	RegulatoryDocumentsFormProps,
} from './RegulatoryDocumentsForm/RegulatoryDocumentsForm';
import {
	RegulatoryDocumentFormInfo,
	useRegulatoryDocumentForm,
} from './RegulatoryDocumentsForm/useRegulatoryDocumentForm';
import {RegulatoryDocumentFormMode} from './RegulatoryDocumentsForm/useRegulatoryDocumentFormMode';
import {FormRegulatoryDoc} from './RegulatoryDocumentsForm/RegulatoryDocumentsForm.types';
import {
	getIfDocHasUneditableStatus,
	useEditRegulatoryDocCmd,
} from '../useEditRegulatoryDocCmd';
import {
	getDisabledEditionForMOdifiedVersionTooltip,
	isPogisDocument,
	isRegDocEditVersionDataDisabled,
} from '../regulatoryUtils';

export enum RegulatoryDocumentFormOperation {
	Create,
	Update,
	View,
	Delete,
	Copy,
}

const operationsAsArray: RegulatoryDocumentFormOperation[] = Object.keys(
	RegulatoryDocumentFormOperation,
)
	.filter(key =>
		isNaN(
			Number(
				RegulatoryDocumentFormOperation[
					key as keyof typeof RegulatoryDocumentFormOperation
				],
			),
		),
	)
	.map(k => Number(k));

export interface RegulatoryDocumentsListDocumentFormProps
	extends Pick<
		RegulatoryDocumentsFormProps,
		'regulationId' | 'regulatoryDocuments'
	> {
	operations?: RegulatoryDocumentFormOperation[];
	dashboard?: boolean;
	markets: Market[];
	regulationNumber?: string;
}

export const RegulatoryDocumentsListDocumentForm = ({
	regulationId,
	operations = operationsAsArray,
	dashboard = false,
	markets,
	regulationNumber,
	...other
}: RegulatoryDocumentsListDocumentFormProps) => {
	const formInfo: RegulatoryDocumentFormInfo = useRegulatoryDocumentForm();
	const {
		modeStateInfo: {setMode, mode},
		documentChangeStateInfo: {setDidDocumentChange},
		dialogStateInfo: {showDialog},
	} = formInfo;

	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'RegulatoryDocumentsForm',
	});

	const {selectedRegulatoryDocument, allSelectedRegulatoryDocuments} =
		useRegulatoryDocumentsContext();

	const {isAdmin, username, encodedUserId, isVko, isAvk} = useUserContext();

	/**
	 * Commands
	 */
	useCommand(
		{
			hidden: !operations.includes(RegulatoryDocumentFormOperation.Create),
			key: 'add',
			text:
				selectedRegulatoryDocument &&
				operations.includes(RegulatoryDocumentFormOperation.Copy)
					? t('Copy')
					: t('New'),
			priority: 1,
			iconProps: {
				iconName: 'Add',
			},
			onClick() {
				if (selectedRegulatoryDocument) {
					setMode(RegulatoryDocumentFormMode.Copy);
				} else {
					setMode(RegulatoryDocumentFormMode.Create);
				}
			},
			roles: dashboard
				? [UserRole.SystemAdministrator]
				: [
						UserRole.SystemAdministrator,
						UserRole.Vko,
						UserRole.VkoExternal,
						UserRole.Avk,
				  ],
			disabled:
				(allSelectedRegulatoryDocuments ?? []).length > 1 ||
				isPogisDocument(selectedRegulatoryDocument) ||
				(Boolean(selectedRegulatoryDocument) &&
					!operations.includes(RegulatoryDocumentFormOperation.Copy)),
		},
		[mode, selectedRegulatoryDocument, allSelectedRegulatoryDocuments],
	);

	const getIfSelectedMultipleDocs = (): boolean => {
		return (allSelectedRegulatoryDocuments ?? []).length > 1;
	};

	const isMachineRegulation =
		selectedRegulatoryDocument?.regulation?.isMachineRegulation === true;

	const isRegDocDisabled = useMemo(() => {
		if (!selectedRegulatoryDocument) return true;
		const getHasInvalidStatus = getIfDocHasUneditableStatus;
		const hasInvalidStatus: boolean = getHasInvalidStatus(
			selectedRegulatoryDocument,
			isAdmin,
			username,
		);
		const selectedMultiple: boolean = getIfSelectedMultipleDocs();
		return (
			selectedMultiple ||
			hasInvalidStatus ||
			isPogisDocument(selectedRegulatoryDocument) ||
			isRegDocEditVersionDataDisabled(
				selectedRegulatoryDocument,
				isAdmin,
				isVko,
				encodedUserId,
			) ||
			(isAvk && !isMachineRegulation)
		);
	}, [
		encodedUserId,
		getIfSelectedMultipleDocs,
		isAdmin,
		isAvk,
		isMachineRegulation,
		selectedRegulatoryDocument,
		username,
	]);

	useEditRegulatoryDocCmd({
		setDidDocumentChange,
		setMode,
		hidden: !operations.includes(RegulatoryDocumentFormOperation.Update),
		key: 'edit',
		text: t('Edit'),
		title: getDisabledEditionForMOdifiedVersionTooltip(
			selectedRegulatoryDocument,
			t,
		),
		priority: 2,
		disabled: isRegDocDisabled,
		extraDependencies: [
			mode,
			selectedRegulatoryDocument,
			allSelectedRegulatoryDocuments,
			isAdmin,
		],
	});

	useCommand(
		{
			hidden:
				!operations.includes(RegulatoryDocumentFormOperation.Delete) ||
				(!isAdmin &&
					!(isAvk && isMachineRegulation) &&
					!(
						selectedRegulatoryDocument?.workflow?.status ===
							WorkflowStatus.Modified &&
						(selectedRegulatoryDocument?.createdBy?.id === encodedUserId ||
							selectedRegulatoryDocument?.createdBy?.id === encodedUserId)
					)),
			key: 'delete',
			text: t('Delete'),
			priority: 3,
			iconProps: {
				iconName: 'Delete',
			},
			onClick: () => showDialog(),
			disabled:
				!allSelectedRegulatoryDocuments?.length ||
				allSelectedRegulatoryDocuments?.length > 1 ||
				(isAvk && !isMachineRegulation),
			roles: [UserRole.SystemAdministrator, UserRole.Vko, UserRole.Avk],
		},
		[allSelectedRegulatoryDocuments, isMachineRegulation],
	);

	const refetchQueries = React.useMemo(
		() =>
			regulationId
				? [GetRegulationDocument, GetWorkbenchItemsDocument]
				: [GetHeadlessRegulatoryDocumentsDocument],
		[],
	);

	/**
	 * The regulatory documents list query only loads and fetched lazily for the single
	 * selected document when an edit form is opened.
	 */
	const {data: editData} = useGetRegulatoryDocumentEditFormQuery({
		variables: {regulatoryDocumentId: selectedRegulatoryDocument?.id ?? ''},
		skip:
			!selectedRegulatoryDocument?.id ||
			mode === RegulatoryDocumentFormMode.None ||
			mode === RegulatoryDocumentFormMode.Create,
	});

	const regulatoryDocumentForForm = React.useMemo(() => {
		if (selectedRegulatoryDocument && editData?.regulatoryDocument) {
			return {
				...selectedRegulatoryDocument,
				...editData.regulatoryDocument,
			} as FormRegulatoryDoc;
		}
		return selectedRegulatoryDocument;
	}, [selectedRegulatoryDocument, editData]);

	return (
		<RegulatoryDocumentsForm
			regulatoryDocument={regulatoryDocumentForForm}
			queriesToRefetchAfterSave={refetchQueries}
			regulationId={regulationId}
			markets={markets}
			regulationNumber={regulationNumber ?? ''}
			{...other}
			{...formInfo}
		/>
	);
};
